import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import '../components/RulesTab.css'
import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft';

interface ResultItem {
  part: string
  description: string
  category: string
  reasoning: string
  status?: string
  success?: any
  buffer_stock?: any
  demand_13_wks?: any
  errors?: any
  error?: any
  lead_time?: any
  on_hand?: any
  primary_source?: any
  blocked_on_hand?: any
  aml_data?: any
  no_approved_manufacturers?: any
  other_cms?: any
  part_classification?: any
  receipts_13_wks?: any
  shortage_wk_1?: any
  shortage_wk_2?: any
  shortage_wk_3?: any
  shortage_wk_4?: any
  shortage_wk_5?: any
  shortage_wk_6?: any
  shortage_wk_7?: any
  shortage_wk_8?: any
  shortage_wk_9?: any
  shortage_wk_10?: any
  shortage_wk_11?: any
  shortage_wk_12?: any
  shortage_wk_13?: any
  site?: any
}

interface ResultsData {
  job_id: string
  status: string
  results: ResultItem[]
  summary: any
}

const JobDetails: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>()
  const navigate = useNavigate()
  const [data, setData] = useState<ResultsData | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  // Modal state for displaying a single result in detail
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [selectedResult, setSelectedResult] = useState<ResultItem | null>(null)

  useEffect(() => {
    if (!jobId) return
    fetchJob()
  }, [jobId])

  // Close modal on Escape and prevent background scroll while open
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setModalOpen(false)
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    if (modalOpen) document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [modalOpen])

  const openResultModal = (r: ResultItem) => {
    setSelectedResult(r)
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setSelectedResult(null)
  }

  const fetchJob = async () => {
    try {
      setLoading(true)
      setError(null)
      const resp = await axios.get(`/jobs/${jobId}`)
      const job = resp.data
      // attempt to load results (if available)
      let results = null
      try {
        const r = await axios.get(`/jobs/${jobId}/results`)
        results = r.data
      } catch (e) {
        // ignore - results may not be ready
      }

      setData({ job_id: jobId || '', status: (job && job.status) ? job.status : 'unknown', results: results?.results || [], summary: results?.summary || {} })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load job')
    } finally {
      setLoading(false)
    }
  }

  const goBack = () => {
    navigate('/')
  }

  // General fallback badge mapping (keeps previous behavior)
  const getBadgeClass = (status?: any) => {
    if (status === true || status === 'true' || status === 'True') return 'badge-success'
    if (status === false || status === 'false' || status === 'False') return 'badge-failed'
    if (!status) return 'badge-default'
    const s = String(status).toLowerCase()
    if (s.includes('success') || s.includes('complete')) return 'badge-success'
    if (s.includes('pending')) return 'badge-pending'
    if (s.includes('schedule')) return 'badge-scheduled'
    return 'badge-default'
  }

  // Job-level status color mapping: COMPLETED=green, PENDING=yellow, PROCESSING=purple
  const getJobStatusBadgeClass = (status?: any) => {
    const s = String(status || '').toLowerCase()
    if (s.includes('completed') || s === 'completed') return 'badge-success'
    if (s.includes('pending') || s === 'pending') return 'badge-pending'
    if (s.includes('processing') || s === 'processing') return 'badge-processing'
    return getBadgeClass(status)
  }

  // Category mapping: IGNORE=green, CRITICAL=red, CONSTRAINT=yellow
  const getCategoryBadgeClass = (category?: string) => {
    const s = String(category || '').toLowerCase()
    if (s === 'ignore') return 'badge-success'
    if (s === 'critical') return 'badge-failed'
    if (s === 'constraint') return 'badge-pending'
    return 'badge-default'
  }

  const getSuccessBadgeClass = (val?: any) => {
    if (val === true || String(val).toLowerCase() === 'true') return 'badge-success'
    return 'badge-failed'
  }

  const formatStatusText = (status?: any) => {
    if (status === true || String(status).toLowerCase() === 'true') return 'True'
    if (status === false || String(status).toLowerCase() === 'false') return 'False'
    if (status === undefined || status === null) return 'N/A'
    return String(status)
  }

  // Parse aml_data string like "[('08055C223KAT2A', 'AVX'), ('C0805C223K5RAC', 'KEMET')]" into { mpn, mfg }[]
  const parseAmlData = (raw: any): { mpn: string; mfg: string }[] => {
    if (raw == null || raw === '') return []
    const s = String(raw).trim()
    if (!s) return []
    const pairs: { mpn: string; mfg: string }[] = []
    const regex = /\(\s*'([^']*)'\s*,\s*'([^']*)'\s*\)/g
    let m: RegExpExecArray | null
    while ((m = regex.exec(s)) !== null) {
      pairs.push({ mpn: m[1], mfg: m[2] })
    }
    return pairs
  }

  const shortageWkKeys = ['shortage_wk_1', 'shortage_wk_2', 'shortage_wk_3', 'shortage_wk_4', 'shortage_wk_5', 'shortage_wk_6', 'shortage_wk_7', 'shortage_wk_8', 'shortage_wk_9', 'shortage_wk_10', 'shortage_wk_11', 'shortage_wk_12', 'shortage_wk_13'] as const

  return (
    <div className="job-details-page">
      <div className="rules-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="icon-btn" onClick={goBack} aria-label="Back">
            <ArrowCircleLeftIcon className="icon-left" fontSize="large" />
          </button>
          <div>
            <h2>Job Details</h2>
            <div> 
              <span  style={{ color: '#666' }}>Job ID: {jobId}</span> 
              <span style={{ marginLeft: '1rem'}} className={`badge ${getJobStatusBadgeClass(data?.status)}`}>{data?.status}</span></div>
          </div>
        </div>
      </div>



      <div style={{ marginTop: 12 }}>
        {loading ? (
          <div className="rules-loading"><div className="spinner"></div><p>Loading job...</p></div>
        ) : error ? (
          <div className="rules-error"><div className="error-icon">⚠️</div><p>{error}</p></div>
        ) : data ? (
          <div>
            <div className="stats-cards">
              <div className="stat-card">
                <div className="stat-title">Total Parts</div>
                <div className="stat-value">{data.summary?.total ?? 'N/A'}</div>
              </div>
              <div className="stat-card">
                <div className="stat-title">Successful</div>
                <div className="stat-value">{data.summary?.successful ?? 0}</div>
              </div>
              <div className="stat-card">
                <div className="stat-title">Failed</div>
                <div className="stat-value">{data.summary?.failed ?? 0}</div>
              </div>
              <div className="stat-card">
                <div className="stat-title">Ignore</div>
                <div className="stat-value" style={{ color: 'rgb(87, 130, 43)' }}>{data.summary?.category_counts?.IGNORE ?? 0}</div>
              </div>
              <div className="stat-card">
                <div className="stat-title">Critical</div>
                <div className="stat-value" style={{ color: 'rgb(220, 20, 60)' }}>{data.summary?.category_counts?.CRITICAL ?? 0}</div>
              </div>
              <div className="stat-card">
                <div className="stat-title">Constraint</div>
                <div className="stat-value" style={{ color: 'rgb(255, 193, 7)' }}>{data.summary?.category_counts?.CONSTRAINT ?? 0}</div>
              </div>
            </div>

            {/* <div className="category-stats">

            </div>  */}

            <div style={{ marginTop: 18 }}>
              <h4>Detailed Results</h4>
              {data.results.length === 0 ? (
                <p>No results yet.</p>
              ) : (
                <>
                <div className="table-card">
                  <div style={{ marginBottom: 8, color: '#6b7280', fontSize: 14 }}>Click any row to view full details</div>
                  <table>
                    <thead>
                      <tr>
                        <th>Part</th>
                        <th>On Hand</th>
                        <th>Blocked On Hand</th>
                        <th>Receipts 13 wks</th>
                        <th>Demand 13 wks</th>
                        <th>Buffer Stock</th>
                        <th>Lead Time</th>
                        <th>Primary Source</th>
                        <th>Category</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.results.map((r, i) => (
                        <tr
                          key={i}
                          className="result-row"
                          role="button"
                          tabIndex={0}
                          aria-label={`Open details for ${r.part}`}
                          onClick={() => openResultModal(r)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openResultModal(r) }}
                        >
                          <td>{r.part}</td>
                          <td>{r.on_hand}</td>
                          <td>{r?.blocked_on_hand?? 'N/A'}</td>
                          <td>{r?.receipts_13_wks?? 'N/A'}</td>
                          <td>{r?.demand_13_wks?? 'N/A'}</td>
                          <td>{r?.buffer_stock?? 'N/A'}</td>
                          <td>{r?.lead_time?? 'N/A'}</td>
                          <td>{r?.primary_source?? 'N/A'}</td>
                          <td><span className={`badge ${getCategoryBadgeClass(r.category)}`}>{r.category}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table> 
                </div>

                {modalOpen && selectedResult && (
                  <div className="preview-modal" role="dialog" aria-modal="true" aria-label={`Details for ${selectedResult.part}`} onClick={closeModal}>
                    <div className="preview-modal-content" onClick={(e) => e.stopPropagation()}>
                      <div className="preview-modal-header">
                        <div style={{ alignItems: 'center' }}>
                          <h3 style={{ margin: 0 }}>{selectedResult.part} <span style={{ marginLeft: 10 }} className={`badge ${getCategoryBadgeClass(selectedResult.category)}`}>{selectedResult.category}</span></h3>
                          <div style={{ color: '#6b7280', fontSize: 14, marginTop: 4 }}>{selectedResult.description}</div>
                        </div>
                        <button className="modal-close" aria-label="Close" onClick={closeModal}>✕</button>
                      </div>
                      <div className="modal-body result-detail-modal">
                        <div className="modal-section">
                          <h4 className="modal-section-title">Summary</h4>
                          <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{selectedResult.reasoning ?? 'N/A'}</p>
                        </div>
                        <h4 style={{ marginTop: 12, color: '#1e293b' }}>Decision Inputs</h4>
                        <div className="modal-detail-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                          <div className="detail-row"><strong>Primary Source</strong><div>{selectedResult.primary_source ?? 'N/A'}</div></div>
                          <div className="detail-row"><strong>Lead Time</strong><div>{selectedResult.lead_time ?? 'N/A'}{typeof selectedResult.lead_time === 'string' && !selectedResult.lead_time.includes('day') ? ' days' : ''}</div></div>
                          <div className="detail-row"><strong>On Hand</strong><div>{selectedResult.on_hand ?? 'N/A'}</div></div>
                          <div className="detail-row"><strong>Blocked On Hand</strong><div>{selectedResult.blocked_on_hand ?? 'N/A'}</div></div>
                          <div className="detail-row"><strong>Receipts (13 wks)</strong><div>{selectedResult.receipts_13_wks ?? 'N/A'}</div></div>
                          <div className="detail-row"><strong>Demand (13 wks)</strong><div>{selectedResult.demand_13_wks ?? 'N/A'}</div></div>
                          <div className="detail-row"><strong>Buffer Stock</strong><div>{selectedResult.buffer_stock ?? 'N/A'}</div></div>
                          <div className="detail-row"><strong>Part Classification</strong><div>{selectedResult.part_classification ?? '—'}</div></div>
                          {/* <div className="detail-row"><strong>Other CMS</strong><div>{formatStatusText(selectedResult.other_cms)}</div></div> */}
                          <div className="detail-row"><strong>No. Approved Manufacturers</strong><div>{selectedResult.no_approved_manufacturers ?? 'N/A'}</div></div>
                          <div className="detail-row"><strong>Success</strong><div className={`badge ${getSuccessBadgeClass(selectedResult.success)}`}>{formatStatusText(selectedResult.success)}</div></div>
                          {selectedResult.error ? (
                            <div className="detail-row" style={{ gridColumn: '1 / -1' }}><strong>Error</strong><div style={{ color: '#b91c1c' }}>{selectedResult.error}</div></div>
                          ) : null}
                        </div>

                        <div className="modal-section">
                          <h4 className="modal-section-title">Demand Forecast by Week</h4>
                          <div className="shortage-table-wrap">
                            <table className="shortage-table">
                              <thead>
                                <tr>
                                  {shortageWkKeys.map((key, i) => (
                                    <th key={key}>Wk {i + 1}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  {shortageWkKeys.map((key) => (
                                    <td key={key}>{(selectedResult as any)[key] ?? '—'}</td>
                                  ))}
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {(() => {
                          const amlRows = parseAmlData(selectedResult.aml_data)
                          if (amlRows.length === 0) return null
                          return (
                            <div className="modal-section">
                              <h4 className="modal-section-title">AML Data (Approved Manufacturers)</h4>
                              <div className="aml-table-wrap">
                                <table className="aml-table">
                                  <thead>
                                    <tr>
                                      <th>MPN</th>
                                      <th>MFG</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {amlRows.map((row, i) => (
                                      <tr key={i}>
                                        <td><code className="aml-mpn">{row.mpn}</code></td>
                                        <td>{row.mfg}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )
                        })()}
                      </div>
                    </div>
                  </div>
                )}

              </>) }
            </div>
          </div>
        ) : (
          <p>No job information available.</p>
        )}
      </div>
    </div>
  )
}

export default JobDetails
